// Physical slots stay independent of string routing: moving a module never rewires it.
import { add, sub, mul, dot, cross, norm } from "./vec3";
export const moduleSlot = (p) => `${p.row}:${p.column}:${p.tier}`;
const lerp = (a, b, t) => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, z: a.z + (b.z - a.z) * t });
const corners = (q) => [q.low[0], q.low[1], q.high[1], q.high[0]];
const centreOf = (q) => mul(corners(q).reduce((sum, p) => add(sum, p), { x: 0, y: 0, z: 0 }), 0.25);
const rotateVector = (v, axis, angle) => add(add(mul(v, Math.cos(angle)), mul(cross(axis, v), Math.sin(angle))), mul(axis, dot(axis, v) * (1 - Math.cos(angle))));
function rotateModule(q, degrees) {
  if (!degrees) return q;
  const centre = centreOf(q), axis = norm(cross(sub(q.low[1], q.low[0]), sub(q.high[0], q.low[0])));
  const rotate = (p) => add(centre, rotateVector(sub(p, centre), axis, degrees * Math.PI / 180));
  return { ...q, low: q.low.map(rotate), high: q.high.map(rotate) };
}

function rotateArray(quads, offsets, axis, angle, pivotQuads = quads, groundZ = null) {
  const centre = mul(pivotQuads.reduce((sum, face) => add(sum, centreOf(face)), { x: 0, y: 0, z: 0 }), 1 / pivotQuads.length);
  const next = { ...offsets };
  for (const face of quads) {
    const before = centreOf(face), after = add(centre, rotateVector(sub(before, centre), axis, angle));
    const old = offsets[face.slot] || {};
    next[face.slot] = { ...old, eastM: (Number(old.eastM) || 0) + after.x - before.x,
      northM: (Number(old.northM) || 0) + after.y - before.y,
      rotationDeg: (Number(old.rotationDeg) || 0) + angle * 180 / Math.PI,
      // Raised modules rotate rigidly in THEIR plane, not the supporting
      // roof plane. Account for the terrain-following XY shift separately.
      ...(groundZ ? { elevationM: (Number(old.elevationM) || 0) + after.z - before.z
        - (groundZ(after.x - before.x, after.y - before.y) - groundZ(0, 0)) } : {}) };
  }
  return next;
}

// Rigid rotation IN the module plane: keeps dimensions, spacing, wiring and
// surface normal unchanged. Raised arrays also store the vertical correction
// relative to their supporting roof's terrain-following offsets.
export function alignModulesToEdge(quads, edge, offsets = {}, groundZ = () => 0, direction = "across", pivotQuads = quads, raised = false) {
  if (!quads.length || !edge || edge.length !== 2) return offsets;
  const q = pivotQuads[0], widthAxis = norm(sub(q.low[1], q.low[0]));
  const lengthAxis = norm(sub(q.high[0], q.low[0]));
  const axis = norm(cross(widthAxis, lengthAxis));
  // The current behaviour places the row/width axis along the edge. The
  // alternate mode places the module's front-to-back axis along it instead.
  const along = direction === "along" ? lengthAxis : widthAxis;
  const dx = edge[1].x - edge[0].x, dy = edge[1].y - edge[0].y;
  // Lift the plan edge into the module plane. Using roof elevations here
  // would not align its projected edge when the two inclinations differ.
  const edgeVector = { x: dx, y: dy, z: -(axis.x * dx + axis.y * dy) / axis.z };
  if (Math.hypot(edgeVector.x, edgeVector.y, edgeVector.z) < 1e-8) return offsets;
  const target = norm(edgeVector);
  let angle = Math.atan2(dot(axis, cross(along, target)), dot(along, target));
  // An edge is a line, not a facing: choose the smaller rotation (<= 90°).
  if (angle > Math.PI / 2) angle -= Math.PI;
  if (angle < -Math.PI / 2) angle += Math.PI;
  return rotateArray(quads, offsets, axis, angle, pivotQuads, raised ? groundZ : null);
}

/** Set the front-to-back direction of a PV group to any plan bearing.
 * Unlike edge alignment, this is directed through the full circle so dragging
 * the handle cannot flip the array abruptly at a 90° half-turn boundary.
 * Module normal, dimensions and slot IDs stay put.
 */
function axisTurn(quads, bearingDeg, axisKind = "length") {
  const q = quads[0], widthAxis = norm(sub(q.low[1], q.low[0]));
  const lengthAxis = norm(sub(q.high[0], q.low[0]));
  const along = axisKind === "width" ? widthAxis : lengthAxis;
  const axis = norm(cross(widthAxis, lengthAxis));
  const bearing = Number(bearingDeg) * Math.PI / 180;
  const dx = Math.sin(bearing), dy = Math.cos(bearing);
  const target = norm({ x: dx, y: dy,
    // The displayed modules define the plane being turned. The roof terrain
    // can temporarily have a different bearing while its orientation is being
    // edited; using that bearing here made the saved offsets disagree with the
    // on-plan preview and opened gaps between adjacent modules.
    z: -(axis.x * dx + axis.y * dy) / axis.z });
  const angle = Math.atan2(dot(axis, cross(along, target)), dot(along, target));
  return { axis, angle };
}

// The same rigid transform is used for the on-plan preview and saved offsets.
export function rotatedModuleQuadsToAxis(quads, bearingDeg) {
  if (!quads.length || !Number.isFinite(Number(bearingDeg))) return quads;
  const { axis, angle } = axisTurn(quads, bearingDeg);
  const centre = mul(quads.reduce((sum, face) => add(sum, centreOf(face)), { x: 0, y: 0, z: 0 }), 1 / quads.length);
  const rotate = (p) => add(centre, rotateVector(sub(p, centre), axis, angle));
  return quads.map((q) => ({ ...q, low: q.low.map(rotate), high: q.high.map(rotate) }));
}

export function rotateModulesToAxis(quads, bearingDeg, offsets = {}, axisKind = "length", pivotQuads = quads, groundZ = null) {
  if (!quads.length || !Number.isFinite(Number(bearingDeg))) return offsets;
  const { axis, angle } = axisTurn(pivotQuads, bearingDeg, axisKind);
  return rotateArray(quads, offsets, axis, angle, pivotQuads, groundZ);
}

// A group turn is a property of the ARRAY, not a set of independent module
// moves. Persist its plan bearing and only the residuals explicitly edited in
// 3D. Rebuild the rigid turn on the current roof each time, including on load.
function rowSpacing(base) {
  const first = base[0], second = base.find((q) => q.position.row === first?.position.row + 1);
  return second ? dot(sub(centreOf(second), centreOf(first)), norm(sub(first.high[0], first.low[0]))) : undefined;
}
function packedRoofOffsets(base, array, groundZ) {
  if (!base.length) return {};
  const step = rowSpacing(base);
  const delta = Number.isFinite(array.rowSpacingM) && Number.isFinite(step) ? array.rowSpacingM - step : 0;
  const along = norm(sub(base[0].high[0], base[0].low[0]));
  const meanRow = base.reduce((sum, q) => sum + q.position.row, 0) / base.length;
  const shifts = new Map(base.map((q) => [q.slot, mul(along, delta * (q.position.row - meanRow))]));
  const compact = base.map((q) => shiftModule(q, shifts.get(q.slot), groundZ));
  const offsets = rotateModulesToAxis(compact, array.axisBearingDeg, {}, array.axis);
  for (const q of base) {
    offsets[q.slot].eastM += shifts.get(q.slot).x;
    offsets[q.slot].northM += shifts.get(q.slot).y;
  }
  return offsets;
}
export function roofModuleArray(base, offsets, groundZ, axisBearingDeg, previous) {
  if (!base.length) return null;
  const placed = base.map((q) => shiftModule(rotateModule(q, offsets[q.slot]?.rotationDeg), {
    x: Number(offsets[q.slot]?.eastM) || 0, y: Number(offsets[q.slot]?.northM) || 0,
    elevationM: Number(offsets[q.slot]?.elevationM) || 0,
  }, groundZ));
  const axisKind = previous?.axis === "width" ? "width" : "length";
  const direction = sub(axisKind === "width" ? placed[0].low[1] : placed[0].high[0], placed[0].low[0]);
  const bearing = axisBearingDeg ?? Math.atan2(direction.x, direction.y) * 180 / Math.PI;
  const array = { version: 1, axis: axisKind, axisBearingDeg: bearing,
    ...(Number.isFinite(previous?.rowSpacingM ?? rowSpacing(base))
      ? { rowSpacingM: previous?.rowSpacingM ?? rowSpacing(base) } : {}) };
  const packed = packedRoofOffsets(base, array, groundZ);
  const individualOffsets = {};
  for (const q of base) {
    const a = offsets[q.slot] || {}, b = packed[q.slot] || {};
    const residual = {
      eastM: (Number(a.eastM) || 0) - (Number(b.eastM) || 0),
      northM: (Number(a.northM) || 0) - (Number(b.northM) || 0),
      rotationDeg: (Number(a.rotationDeg) || 0) - (Number(b.rotationDeg) || 0),
      ...((a.elevationM || b.elevationM) ? { elevationM: (Number(a.elevationM) || 0) - (Number(b.elevationM) || 0) } : {}),
    };
    if (Object.values(residual).some((n) => Math.abs(n) > 1e-8)) individualOffsets[q.slot] = residual;
  }
  return { ...array, individualOffsets };
}

export function resolveRoofModuleOffsets(rows, tiers, spacing, groundZ) {
  const array = spacing?.module_array;
  if (!array || array.version !== 1 || !Number.isFinite(array.axisBearingDeg)) return spacing?.module_offsets || {};
  const base = moduleQuads(rows, tiers, {}, groundZ);
  const packed = packedRoofOffsets(base, array, groundZ);
  for (const [slot, residual] of Object.entries(array.individualOffsets || {})) {
    if (!packed[slot]) continue;
    packed[slot] = { ...packed[slot],
      eastM: packed[slot].eastM + (Number(residual.eastM) || 0),
      northM: packed[slot].northM + (Number(residual.northM) || 0),
      rotationDeg: packed[slot].rotationDeg + (Number(residual.rotationDeg) || 0),
      ...(residual.elevationM ? { elevationM: (Number(packed[slot].elevationM) || 0) + residual.elevationM } : {}) };
  }
  return packed;
}

// Older edge/free rotations wrote one equal turn plus XY compensation per
// slot, without recording the roof basis. Those compensation vectors cannot
// safely be replayed on a changed roof. Recover the bearing, rebuild packed,
// and leave sparse legacy individual edits alone.
export function legacyRoofModuleArray(base, offsets, groundZ) {
  const values = base.map((q) => offsets?.[q.slot]);
  if (base.length < 2 || Object.keys(offsets || {}).length !== base.length ||
      values.some((v) => !Number.isFinite(v?.rotationDeg)) ||
      values.some((v) => Math.abs(v.rotationDeg - values[0].rotationDeg) > 1e-7)) return null;
  const array = roofModuleArray(base, offsets, groundZ);
  return { ...array, individualOffsets: {} };
}

export function shiftModule(q, delta, groundZ = () => 0) {
  const z = groundZ(delta.x, delta.y) - groundZ(0, 0) + (Number(delta.elevationM) || 0);
  const shift = (p) => ({ x: p.x + delta.x, y: p.y + delta.y, z: p.z + z });
  return { ...q, low: q.low.map(shift), high: q.high.map(shift) };
}
export function moduleQuad(row, p, tiers = 1, offsets = {}, groundZ) {
  const point = (c, t) => lerp(lerp(row.low[0], row.low[1], c / row.modules), lerp(row.high[0], row.high[1], c / row.modules), t / tiers);
  const key = moduleSlot(p), offset = offsets[key] || {};
  return shiftModule(rotateModule({ ...row, slot: key, position: p, modules: 1,
    low: [point(p.column, p.tier), point(p.column + 1, p.tier)],
    high: [point(p.column, p.tier + 1), point(p.column + 1, p.tier + 1)] }, Number(offset.rotationDeg) || 0),
  { x: Number(offset.eastM) || 0, y: Number(offset.northM) || 0, elevationM: Number(offset.elevationM) || 0 }, groundZ);
}
// A slot taken out of the plan by hand is skipped, not shifted: the quads keep
// the grid's coordinates so offsets on the remaining modules stay meaningful.
export function moduleQuads(rows, tiers = 1, offsets = {}, groundZ, excluded = null) {
  const gone = excluded && excluded.length ? new Set(excluded) : null;
  return rows.flatMap((row, i) => Array.from({ length: row.modules * tiers }, (_, j) => {
    const p = { row: i + 1, column: Math.floor(j / tiers), tier: j % tiers };
    return gone?.has(moduleSlot(p)) ? null : moduleQuad(row, p, tiers, offsets, groundZ);
  }).filter(Boolean));
}
export function layoutGeometry(geo, tiers, offsets, groundZ, force = false, excluded = null) {
  if (!geo || (!force && !Object.keys(offsets || {}).length && !(excluded && excluded.length))) return geo;
  return { ...geo, individualModules: true, rows: moduleQuads(geo.rows, tiers, offsets, groundZ, excluded) };
}
const interval = (q, axis) => {
  const values = corners(q).map((p) => p.x * axis.x + p.y * axis.y);
  return [Math.min(...values), Math.max(...values)];
};
const axes = (q) => [q.low[1], q.high[0]].map((p) => {
  const x = p.x - q.low[0].x, y = p.y - q.low[0].y, length = Math.hypot(x, y);
  return { x: -y / length, y: x / length };
});
export function modulesOverlap(a, b) {
  return [...axes(a), ...axes(b)].every((axis) => {
    const [a0, a1] = interval(a, axis), [b0, b1] = interval(b, axis);
    return Math.min(a1, b1) - Math.max(a0, b0) > 0.001;
  });
}
export function overlapCount(quads) {
  let count = 0;
  const sorted = quads.map((q) => ({ q, x: interval(q, { x: 1, y: 0 }), y: interval(q, { x: 0, y: 1 }) }))
    .sort((a, b) => a.x[0] - b.x[0]);
  for (let i = 0; i < sorted.length; i++) for (let j = i + 1; j < sorted.length && sorted[j].x[0] < sorted[i].x[1] - 0.001; j++) {
    if (Math.min(sorted[i].y[1], sorted[j].y[1]) - Math.max(sorted[i].y[0], sorted[j].y[0]) > 0.001 && modulesOverlap(sorted[i].q, sorted[j].q)) count++;
  }
  return count;
}
/**
 * Snap a moved module to its neighbours, in roof metres.
 *
 * First to a SLOT: the place right beside a neighbour (left, right, above or
 * below it) at the gap, lined up with it end to end, as the modules of a
 * row sit. A module dropped within about half its size of such a place goes
 * there exactly, so there is no sliver of a gap and no step. Otherwise,
 * within the capture radius of a neighbour's edge, it lands at the gap from
 * that edge and lines up with the neighbour's end when the ends are within
 * `alignFraction` of the module's length. Alt while dragging bypasses all of
 * it. The result names the neighbour it snapped to (`target`, a slot key)
 * and how (`kind`: "slot" or "edge"), for the picture to show it.
 */
export function snapModuleDelta(quads, selected, delta, { threshold = 0.15, gap = 0.02, groundZ, valid = () => true,
  alignFraction = 0.45, slotFraction = 0.4 } = {}) {
  const chosen = new Set(selected);
  const moving = quads.filter((q) => chosen.has(q.slot));
  const allFixed = quads.filter((q) => !chosen.has(q.slot));
  if (!moving.length || !allFixed.length) return { delta, snapped: false };
  const a = moving[0], along = { x: a.low[1].x - a.low[0].x, y: a.low[1].y - a.low[0].y };
  const n = Math.hypot(along.x, along.y), u = { x: along.x / n, y: along.y / n }, v = { x: -u.y, y: u.x };
  // Only modules near where the moving ones are going can be snapped to or
  // collide with them. On a 708-module roof every move used to test all 707
  // others - twice for candidates and again for each overlap check - which
  // was the largest single cost of dragging a panel. Plan boxes, widened by
  // twice the module's own size plus the capture radius, keep every module
  // that could yield a slot, an edge or an overlap and drop the rest.
  const box = (q) => { const xs = corners(q).map((p) => p.x), ys = corners(q).map((p) => p.y);
    return [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)]; };
  const movedNow = moving.map((q) => shiftModule(q, delta, groundZ));
  const extent = Math.max(...movedNow.map((q) => { const b = box(q); return Math.max(b[1] - b[0], b[3] - b[2]); }));
  const reach = 2 * extent + threshold + gap;
  const fixedBoxes = allFixed.map(box);
  const near = (b, r) => allFixed.filter((q, i) => fixedBoxes[i][0] <= b[1] + r && fixedBoxes[i][1] >= b[0] - r
    && fixedBoxes[i][2] <= b[3] + r && fixedBoxes[i][3] >= b[2] - r);
  const span = movedNow.map(box).reduce((acc, b) => [Math.min(acc[0], b[0]), Math.max(acc[1], b[1]), Math.min(acc[2], b[2]), Math.max(acc[3], b[3])]);
  const fixed = near(span, reach);
  if (!fixed.length) return { delta, snapped: false };
  const fits = (candidate) => {
    const shifted = moving.map((q) => shiftModule(q, candidate, groundZ));
    // A bounding-box miss is a proof of no overlap, so only boxes that touch
    // are given the exact test.
    return !shifted.some((q) => near(box(q), 0.001).some((other) => modulesOverlap(q, other))) && valid(shifted);
  };
  // Slots: beside a neighbour, flush with it along the shared side.
  const slots = [];
  for (const moved of movedNow) for (const target of fixed) {
    for (const [normal, tangent] of [[u, v], [v, u]]) {
      const m = interval(moved, normal), t = interval(target, normal);
      const mt = interval(moved, tangent), tt = interval(target, tangent);
      const reachN = Math.max(threshold, (m[1] - m[0]) * slotFraction);
      const reachT = Math.max(threshold, (mt[1] - mt[0]) * slotFraction);
      for (const dn of [t[0] - gap - m[1], t[1] + gap - m[0]]) {
        if (Math.abs(dn) > reachN) continue;
        for (const dt of [tt[0] - mt[0], tt[1] - mt[1]]) {
          if (Math.abs(dt) > reachT) continue;
          slots.push({ delta: { x: delta.x + normal.x * dn + tangent.x * dt, y: delta.y + normal.y * dn + tangent.y * dt },
            score: (dn / reachN) ** 2 + (dt / reachT) ** 2, target: target.slot });
        }
      }
    }
  }
  for (const slot of slots.sort((p, q) => p.score - q.score)) {
    if (fits(slot.delta)) return { delta: slot.delta, snapped: true, kind: "slot", target: slot.target };
  }
  const candidates = [];
  for (const moved of movedNow) for (const target of fixed) {
    for (const [normal, tangent] of [[u, v], [v, u]]) {
      const m = interval(moved, normal), t = interval(target, normal);
      const mt = interval(moved, tangent), tt = interval(target, tangent);
      if (Math.min(mt[1], tt[1]) < Math.max(mt[0], tt[0]) - threshold) continue;
      for (const dn of [t[0] - gap - m[1], t[1] + gap - m[0]]) {
        if (Math.abs(dn) > threshold) continue;
        // Ends line up within a good part of the module's own length, not
        // only within the capture radius: beside a neighbour is on its grid.
        const alignTolerance = Math.max(threshold, (mt[1] - mt[0]) * alignFraction);
        const alignments = [tt[0] - mt[0], tt[1] - mt[1]].filter((d) => Math.abs(d) <= alignTolerance);
        const dt = alignments.sort((x, y) => Math.abs(x) - Math.abs(y))[0] || 0;
        // Near corners alone are not neighbouring edges: require a shared edge span.
        if (Math.min(mt[1] + dt, tt[1]) - Math.max(mt[0] + dt, tt[0]) <= 0.001) continue;
        const next = { x: delta.x + normal.x * dn + tangent.x * dt, y: delta.y + normal.y * dn + tangent.y * dt };
        candidates.push({ delta: next, aligned: alignments.length > 0, distance: Math.hypot(dn, dt), target: target.slot });
      }
    }
  }
  for (const candidate of candidates.sort((a, b) => Number(b.aligned) - Number(a.aligned) || a.distance - b.distance)) {
    if (fits(candidate.delta)) return { delta: candidate.delta, snapped: true, kind: candidate.aligned ? "slot" : "edge", target: candidate.target };
  }
  return { delta, snapped: false };
}
